// ApplicationsDowntimeChart.jsx
import React from 'react';
import PropTypes from 'prop-types';
import ReactECharts from 'echarts-for-react';
import { createStripePattern } from './Helpers.js';

const BarCodeChart = ({ appData, statusConfig }) => {
  const allEndTimes = appData.flatMap(app => app.timeline.map(t => t.toDate));
  const allStartTimes = appData.flatMap(app => app.timeline.map(t => t.fromDate));
  const maxGlobalToDate = Math.max(...allEndTimes);
  const minGlobalFromDate = Math.min(...allStartTimes);

  const seriesData = [];
  const yAxisLabels = [];

  // Generate pattern fills per status
  const statusPatternMap = {};
  Object.keys(statusConfig).forEach((status) => {
    statusPatternMap[status] = createStripePattern(statusConfig[status].color);
  });

  appData.forEach((app, index) => {
    yAxisLabels.push(app.appName);
    app.timeline.forEach((entry) => {
      seriesData.push({
        name: app.appName,
        value: [index, entry.fromDate, entry.toDate, entry.status],
        itemStyle: {
          color: statusPatternMap[entry.status] || "#ccc"
        }
      });
    });
  });

  const statusKeys = Object.keys(statusConfig);

  const option = {
    tooltip: {
      formatter: (params) => {
        const { value } = params;
        const from = new Date(value[1]).toLocaleString();
        const to = new Date(value[2]).toLocaleString();
        return `
          <b>${params.name}</b><br/>
          Status: ${value[3].charAt(0).toUpperCase() + value[3].slice(1)}<br/>
          From: ${from}<br/>
          To: ${to}
        `;
      }
    },
    legend: {
      data: statusKeys,
      top: 10,
      left: 'left',
      icon: "circle",
      selectedMode: true,
      orient: "horizontal",
      textStyle: {
        color: "#333",
        fontSize: 12
      },
      formatter: (name) => statusConfig[name]?.label || name
    },
    grid: {
      left: 200,
      right: 120,
      top: 40,
      bottom: 50
    },
    xAxis: {
      type: "time",
      min: minGlobalFromDate - 30 * 60 * 1000,
      max: maxGlobalToDate + 6 * 60 * 60 * 1000,
      axisLabel: {
        formatter: (value) => {
          const date = new Date(value);
          return date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric"
          });
        }
      }
    },
    yAxis: {
      type: "category",
      data: yAxisLabels,
      axisLabel: {
        fontWeight: "bold",
        margin: 10
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: "#e0e0e0",
          width: 1,
          type: "solid"
        }
      }
    },
    series: [
      // ✅ Background bars to visualize full range even if no data
      {
        type: "custom",
        name: "Background",
        silent: true,
        renderItem: function (params, api) {
          const categoryIndex = api.value(0);
          const start = api.coord([api.value(1), categoryIndex]);
          const end = api.coord([api.value(2), categoryIndex]);
          const height = 30;

          return {
            type: "rect",
            shape: {
              x: start[0],
              y: start[1] - height / 2,
              width: end[0] - start[0],
              height
            },
            style: {
              fill: "#f5f5f5" // Light gray or any subtle color
            }
          };
        },
        encode: {
          x: [1, 2],
          y: 0
        },
        data: appData.map((app, index) => ({
          value: [index, minGlobalFromDate, maxGlobalToDate]
        })),
        z: 0
      },

      // Legend placeholders
      ...statusKeys.map((status) => ({
        name: status,
        type: "bar",
        data: [],
        itemStyle: { color: statusPatternMap[status] }
      })),

      // Main barcode-style status bars
      {
        type: "custom",
        name: "Applications",
        z: 10,
        renderItem: function (params, api) {
          const categoryIndex = api.value(0);
          const start = api.coord([api.value(1), categoryIndex]);
          const end = api.coord([api.value(2), categoryIndex]);
          const height = 30;

          return {
            type: "rect",
            shape: {
              x: start[0],
              y: start[1] - height / 2,
              width: end[0] - start[0],
              height
            },
            style: {
              fill: statusPatternMap[api.value(3)] || "#ccc",
              stroke: "#fff",
              lineWidth: 0.5
            }
          };
        },
        encode: {
          x: [1, 2],
          y: 0
        },
        data: seriesData
      },

      // Downtime labels
      {
        type: "scatter",
        symbol: "none",
        coordinateSystem: "cartesian2d",
        data: appData.map((app, idx) => {
          const lastEnd = Math.max(...(app.timeline || []).map(t => t.toDate));
          return {
            value: [lastEnd + 10 * 60 * 1000, idx],
            label: {
              show: true,
              position: "right",
              formatter: () => "Downtime",
              color: "#333",
              fontSize: 12,
              fontWeight: "bold",
              padding: [0, 0, 0, 5],
              align: "left"
            }
          };
        }),
        z: 100
      }
    ]
  };

  return <ReactECharts option={option} style={{ height: '400px', width: '100%' }} />;
};

BarCodeChart.propTypes = {
  appData: PropTypes.array.isRequired,
  statusConfig: PropTypes.object.isRequired
};

export default BarCodeChart;



